import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { EmptyState } from "@/components/shared/empty-state";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import { postApiAuditEvents } from "@/lib/api/generated/audit/audit";
import type { PostApiAuditEvents200Item } from "@/lib/api/generated/control-api.schemas";
import { formatTimestamp } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useAuth } from "@/features/auth/auth-provider";
import { useAuditUiStore } from "@/stores/audit-ui-store";

function truncateJson(obj: Record<string, unknown>, maxLength = 80): string {
  const str = JSON.stringify(obj);
  if (str.length <= maxLength) {return str;}
  return str.slice(0, maxLength) + "...";
}

export function AuditPage() {
  useDocumentTitle("Audit");
  const { tenant } = useAuth();

  const { eventType, resourceType, limit, setEventType, setResourceType, setLimit } = useAuditUiStore();
  const [selectedEvent, setSelectedEvent] = useState<PostApiAuditEvents200Item | null>(null);
  const tenantId = tenant?.tenantId;
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["audit-events", tenantId, eventType ?? "", resourceType ?? "", limit],
    queryFn: () =>
      postApiAuditEvents({
        tenantId,
        eventType,
        resourceType,
        limit,
      }),
    enabled: Boolean(tenantId),
  });

  return (
    <div className="flex flex-col gap-4">
      <h1
        className="text-lg font-semibold"
        style={{ color: "var(--text-strong)" }}
      >
        Audit
      </h1>

      {/* Filter controls */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-4">
            {tenant && (
              <div className="flex flex-col gap-1.5">
                <Label>Tenant</Label>
                <div
                  className="h-10 min-w-48 rounded-[var(--radius-sm)] border border-border px-3 flex items-center text-xs font-mono"
                  style={{ background: "var(--bg-elevated)", color: "var(--muted-foreground)" }}
                >
                  {tenant.tenantId}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="audit-event-type">Event type</Label>
              <Input
                id="audit-event-type"
                placeholder="Filter by event type"
                className="w-48"
                value={eventType ?? ""}
                onChange={(e) => setEventType(e.target.value || undefined)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="audit-resource-type">Resource type</Label>
              <Input
                id="audit-resource-type"
                placeholder="Filter by resource type"
                className="w-48"
                value={resourceType ?? ""}
                onChange={(e) => setResourceType(e.target.value || undefined)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Limit</Label>
              <Select
                value={String(limit)}
                onValueChange={(val) => setLimit(Number(val))}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading ? (
        <TableSkeleton rows={5} cols={6} />
      ) : events.length === 0 ? (
        <EmptyState
          title="No audit events"
          description="Audit events will appear here when activity is recorded."
        />
      ) : (
        <div className="border border-border rounded-[var(--radius-md)] overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: "var(--bg-accent)" }}>
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  Time
                </th>
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  Tenant
                </th>
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  Actor
                </th>
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  Event Type
                </th>
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  Resource
                </th>
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr
                  key={event.id}
                  className="border-t border-border cursor-pointer hover:bg-[var(--bg-hover)] transition-colors"
                  onClick={() => setSelectedEvent(event)}
                >
                  <td
                    className="px-4 py-2.5"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {formatTimestamp(event.createdAt)}
                  </td>
                  <td
                    className="px-4 py-2.5 font-mono"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {event.tenantId}
                  </td>
                  <td className="px-4 py-2.5">
                    {event.actor.displayName ?? event.actor.actorId}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline">{event.eventType}</Badge>
                  </td>
                  <td
                    className="px-4 py-2.5 font-mono"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {event.resourceType}:{event.resourceId}
                  </td>
                  <td
                    className="px-4 py-2.5 font-mono max-w-[200px] truncate"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {truncateJson(event.details)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Sheet */}
      <Sheet
        open={selectedEvent !== null}
        onOpenChange={(open) => {
          if (!open) {setSelectedEvent(null);}
        }}
      >
        <SheetContent side="right">
          {selectedEvent && (
            <>
              <SheetHeader>
                <SheetTitle>Audit Event Details</SheetTitle>
                <SheetDescription>{selectedEvent.id}</SheetDescription>
              </SheetHeader>
              <div className="flex flex-col gap-4 px-4 pb-4 overflow-y-auto">
                {/* Structured field list */}
                <DetailRow label="Time" value={formatTimestamp(selectedEvent.createdAt)} />
                <DetailRow label="Tenant" value={selectedEvent.tenantId} mono />
                <div className="flex flex-col gap-0.5">
                  <span
                    className="text-xs font-medium"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    Actor
                  </span>
                  <span className="text-sm" style={{ color: "var(--text-strong)" }}>
                    {selectedEvent.actor.displayName ?? selectedEvent.actor.actorId}
                  </span>
                  <span
                    className="text-xs font-mono"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {selectedEvent.actor.actorId} &middot; {selectedEvent.actor.actorType}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span
                    className="text-xs font-medium"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    Event Type
                  </span>
                  <div>
                    <Badge variant="outline">{selectedEvent.eventType}</Badge>
                  </div>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span
                    className="text-xs font-medium"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    Resource
                  </span>
                  <span className="text-sm font-mono" style={{ color: "var(--text-strong)" }}>
                    {selectedEvent.resourceType} / {selectedEvent.resourceId}
                  </span>
                </div>

                {/* Details JSON viewer */}
                <div className="flex flex-col gap-1">
                  <span
                    className="text-xs font-medium"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    Details
                  </span>
                  <pre
                    className="text-xs font-mono whitespace-pre-wrap break-all rounded-[var(--radius-sm)] p-3 border border-border"
                    style={{ background: "var(--bg-muted)" }}
                  >
                    {JSON.stringify(selectedEvent.details, null, 2)}
                  </pre>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className="text-xs font-medium"
        style={{ color: "var(--muted-foreground)" }}
      >
        {label}
      </span>
      <span className={`text-sm ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
